// MOAT-01 — renders one pilot report (merchant page and Kernvel-staff page share it).
import { decisionText, formatDuration, stopReasonText, type ArmResult, type PilotReport } from "../pilot";

function ArmRow({ label, arm }: { label: string; arm: ArmResult }) {
  return (
    <tr>
      <td>{label}</td>
      <td>{arm.listings_created}</td>
      <td>{arm.approved}</td>
      <td>{arm.pending_censored}</td>
      <td>{arm.excluded}</td>
      <td>{formatDuration(arm.median_seconds)}</td>
    </tr>
  );
}

export default function PilotReportView({ report }: { report: PilotReport }) {
  const { pilot, metric, assistance } = report;
  const cfg = pilot.config;
  return (
    <div>
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-cust">
          {pilot.pilot_key} · v{pilot.version} · <b>{pilot.status}</b>
        </div>
        <p className="risk-sub">
          Workflow: AI draft assistance for vendor listings. Exposure: {cfg.exposure}. Control: {cfg.control}.
        </p>
        <p className="risk-sub">
          Started {pilot.activated_at ?? "—"} · planned end {pilot.ends_at ?? "—"} · stop:{" "}
          {stopReasonText(pilot.stop_reason)} {pilot.stopped_at ? `(${pilot.stopped_at})` : ""}
        </p>
        <p className="risk-sub">
          Frozen config {pilot.config_digest.slice(0, 16)}… · {cfg.duration_days} days or {cfg.target_listings_per_arm}{" "}
          approved listings per group · cap {cfg.budget.max_ai_units_per_shop_per_day} AI units/day · at most{" "}
          {cfg.assignment.max_vendors_per_arm} vendors per group
        </p>
      </div>
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-cust">Declared metric: median time from listing draft to your approval</div>
        <p>
          <b>{decisionText(metric)}</b>
        </p>
        <table className="cash-table">
          <thead>
            <tr>
              <th>Group</th>
              <th>Listings</th>
              <th>Approved</th>
              <th>Still pending</th>
              <th>Excluded</th>
              <th>Median time to approval</th>
            </tr>
          </thead>
          <tbody>
            <ArmRow label="With AI assistance" arm={metric.arms.exposed} />
            <ArmRow label="Without (control)" arm={metric.arms.control} />
          </tbody>
        </table>
        <p className="risk-sub">
          Difference (with − without): {formatDuration(metric.difference_seconds)}
          {metric.relative_change_pct !== null ? ` (${metric.relative_change_pct}%)` : ""} ·{" "}
          {metric.confidence_level_pct}% interval: {formatDuration(metric.ci_low_seconds)} to{" "}
          {formatDuration(metric.ci_high_seconds)}
        </p>
      </div>
      <div className="card">
        <div className="card-cust">Assistance activity (descriptive, not the metric)</div>
        <p className="risk-sub">
          Vendors by group:{" "}
          {Object.entries(assistance.vendors_by_arm)
            .map(([arm, n]) => `${arm} ${n}`)
            .join(" · ") || "none yet"}{" "}
          · assistance calls {assistance.assist_calls} · error rate{" "}
          {assistance.assist_error_rate_bp === null ? "—" : `${(assistance.assist_error_rate_bp / 100).toFixed(2)}%`}
        </p>
      </div>
    </div>
  );
}
